import React, { useEffect, useState } from 'react'

import {
  anyPass,
  assoc,
  chain,
  curry,
  groupBy,
  isEmpty,
  isNil,
  keys,
  map,
  prop,
  reduce,
  zipObj,
} from 'ramda'
import { useSelector } from 'react-redux'
import { Redirect, Route } from 'react-router-dom'

import { access as ACCESS } from '@driftt/traffic-control'

import { AdminOrgs } from 'constants/RoutingConstants'

export const pause = async (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const reduxConstantFactory = (namespace) => (constant) =>
  `@app/settings/${namespace}/${constant}`

export const tempId = () => `_temp_${Math.random().toString(12).substr(2)}`
export const isTempId = (id) =>
  typeof id === 'string' && id.startsWith('_temp_')

const isAdminOrg = (orgId) => Object.values(AdminOrgs).includes(orgId)

export const isEltVisible = (elt) => {
  const bounding = elt.getBoundingClientRect()
  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <=
      (window.innerWidth || document.documentElement.clientWidth)
  )
}

export const byId = curry((list, propType = 'id') =>
  chain(zipObj, map(prop(propType)))(list)
)
export const isNilOrEmpty = anyPass([isNil, isEmpty])

export const ProtectedRoute = ({
  Component,
  key,
  path,
  redirectPath,
  requireAdminOrg,
  orgId,
  requireEnterpriseWidget,
  appConfig,
  requiredAccess,
  requireWorkspaces,
}) => {
  useEffect(() => {
    window.analytics && window.analytics.page && window.analytics.page()
  }, [path])
  const { hasEnterpriseWidget } = useSelector((state) => {
    return {
      hasEnterpriseWidget: state.multiEmbed.hasMultiEmbed,
    }
  })
  return (
    <Route
      key={key}
      path={path}
      render={(props) => {
        // Deny access if user's app-config does not allow for specific page access
        if (!!appConfig && !!requiredAccess && !appConfig[requiredAccess]) {
          return <Redirect from="/" to={redirectPath} />
        }
        // Deny access if route requires an admin org
        if (requireAdminOrg && !isAdminOrg(orgId)) {
          return <Redirect from="/" to={redirectPath} />
        }
        // Deny access if need enterprise widget cutover
        if (!!requireEnterpriseWidget && !hasEnterpriseWidget) {
          return <Redirect from="/" to={'/widget'} />
        }
        // Deny access if need workspace management cutover
        if (!!requireWorkspaces && !ACCESS.hasGates('workspace_settings')) {
          return <Redirect from="/" to={redirectPath} />
        }
        return <Component {...props} />
      }}
    />
  )
}

// hook to use debounce
export function useDebouncedValue(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [delay, value])

  return debouncedValue
}

export function deepValue(obj, path) {
  const pathArr = path.split('.')
  for (let i = 0, len = pathArr.length; i < len; i++) {
    obj = obj[pathArr[i]]
  }
  return obj
}

export const groupById = groupBy(prop('id'))

export const renameKeys = curry((keysMap, obj) =>
  reduce((acc, key) => assoc(keysMap[key] || key, obj[key], acc), {}, keys(obj))
)

export const getKeyByValue = (object, value) =>
  Object.keys(object).find((key) => object[key].includes(value))
